import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  ImageIcon,
  FileIcon,
  Search,
  Upload,
  Loader2,
  CheckSquare,
  Square,
  Folder,
  Sparkles,
  Check,
  X
} from 'lucide-react';
import { useClient } from '../../contexts/ClientContext';
import mediaService from '../../services/mediaService';
import { getApiUrl } from '../../utils/config';
import AuthenticatedImage from './AuthenticatedImage';
import { MediaFile } from './types';
import { motion, AnimatePresence } from 'framer-motion';

interface MultiMediaFileSelectorProps {
  onSelectionChange: (files: MediaFile[]) => void;
  selectedFiles?: MediaFile[];
  acceptedTypes?: string[];
  placeholder?: string;
  onDone?: (files: MediaFile[]) => void;
  selectorId?: string;
  customFilter?: (files: MediaFile[]) => MediaFile[];
  theme?: 'pink' | 'green' | 'orange' | 'brand';
  maxFiles?: number; // Maximum number of files that can be selected
}

export const MultiMediaFileSelector: React.FC<MultiMediaFileSelectorProps> = ({
  onSelectionChange,
  selectedFiles = [],
  acceptedTypes = ['image', 'video', 'audio', 'other'],
  placeholder = "Select media files",
  onDone,
  selectorId = "default",
  customFilter,
  theme = 'pink',
  maxFiles
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [allMediaFiles, setAllMediaFiles] = useState<MediaFile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Get theme colors
  const getThemeColors = () => {
    if (theme === 'green') {
      return {
        gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
        hoverBorder: 'hover:border-emerald-500/50',
        hoverBg: 'from-emerald-500/20 via-teal-500/20 to-cyan-500/20',
        buttonGradient: 'from-emerald-600 via-teal-600 to-cyan-600',
        buttonHover: 'hover:from-emerald-500 hover:via-teal-500 hover:to-cyan-500',
        shadow: 'hover:shadow-emerald-500/25'
      };
    }
    if (theme === 'orange') {
      return {
        gradient: 'from-orange-500 via-amber-500 to-yellow-500',
        hoverBorder: 'hover:border-orange-500/50',
        hoverBg: 'from-orange-500/20 via-amber-500/20 to-yellow-500/20',
        buttonGradient: 'from-orange-600 via-amber-600 to-yellow-600',
        buttonHover: 'hover:from-orange-500 hover:via-amber-500 hover:to-yellow-500',
        shadow: 'hover:shadow-orange-500/25'
      };
    }
    if (theme === 'brand') {
      return {
        gradient: 'from-blue-500 via-purple-500 to-blue-500',
        hoverBorder: 'hover:border-blue-500/50',
        hoverBg: 'from-blue-500/20 via-purple-500/20 to-blue-500/20',
        buttonGradient: 'from-blue-600 via-purple-600 to-blue-600',
        buttonHover: 'hover:from-blue-500 hover:via-purple-500 hover:to-blue-500',
        shadow: 'hover:shadow-blue-500/25'
      };
    }
    // Default pink theme
    return {
      gradient: 'from-primary via-purple-500 to-pink-500',
      hoverBorder: 'hover:border-pink-500/50',
      hoverBg: 'from-primary/20 via-purple-500/20 to-pink-500/20',
      buttonGradient: 'from-primary via-purple-600 to-pink-600',
      buttonHover: 'hover:from-primary/90 hover:via-purple-500 hover:to-pink-500',
      shadow: 'hover:shadow-pink-500/25'
    };
  };

  const themeColors = getThemeColors();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalFiles, setTotalFiles] = useState(0);
  const [internalSelection, setInternalSelection] = useState<MediaFile[]>([]);
  const [, forceUpdate] = useState(0);
  const { currentClient } = useClient();


  // Reset pagination when dialog opens
  useEffect(() => {
    if (isOpen && currentClient) {
      setAllMediaFiles([]);
      setCurrentPage(1);
      setHasNextPage(true);
      setTotalFiles(0);
      setInternalSelection([]); // Clear selection when dialog opens
      fetchMediaFiles(1, true);
    }
  }, [isOpen, currentClient]);

  // Filter files based on accepted types and search term
  useEffect(() => {
    let filtered = allMediaFiles.filter(file => {
      const matchesType = acceptedTypes.includes(file.media_type);
      const matchesSearch = searchTerm === '' ||
        file.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (file.title && file.title.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesType && matchesSearch;
    });

    // Apply custom filter if provided
    if (customFilter) {
      filtered = customFilter(filtered);
    }

    setMediaFiles(filtered);
  }, [searchTerm, JSON.stringify(acceptedTypes), allMediaFiles]);

  // Reset search and reload when search term changes
  useEffect(() => {
    if (isOpen && currentClient) {
      const timeoutId = setTimeout(() => {
        setAllMediaFiles([]);
        setCurrentPage(1);
        setHasNextPage(true);
        fetchMediaFiles(1, true);
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);


  const fetchMediaFiles = async (page: number = 1, reset: boolean = false) => {
    if (!currentClient || (!hasNextPage && !reset)) return;

    if (reset) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await mediaService.getMediaFiles(
        page,
        50, // Load 50 files per page
        searchTerm || undefined,
        currentClient.id
      );

      const sortedFiles = response.files.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      if (reset) {
        setAllMediaFiles(sortedFiles);
      } else {
        setAllMediaFiles(prev => [...prev, ...sortedFiles]);
      }

      setCurrentPage(page);
      setHasNextPage(response.has_next);
      setTotalFiles(response.total);
    } catch (error) {
      console.error('Error fetching media files:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreFiles = () => {
    if (!isLoadingMore && hasNextPage) {
      fetchMediaFiles(currentPage + 1, false);
    }
  };

  const handleFileToggle = (file: MediaFile) => {
    const isSelected = internalSelection.some(f => f.id === file.id);
    if (isSelected) {
      setInternalSelection(internalSelection.filter(f => f.id !== file.id));
    } else {
      // If maxFiles is set and we're at the limit, replace the selection
      if (maxFiles === 1) {
        setInternalSelection([file]);
      } else if (maxFiles && internalSelection.length >= maxFiles) {
        // Don't add more files if at max limit
        return;
      } else {
        setInternalSelection([...internalSelection, file]);
      }
    }
  };

  const handleConfirmSelection = () => {
    onSelectionChange(internalSelection);
    if (onDone) {
      onDone(internalSelection);
    }
    setIsOpen(false);
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getFileIcon = (mediaType: string, mimeType?: string, filename?: string) => {
    // Check for 3D models first
    if (mimeType === 'model/gltf-binary' || filename?.endsWith('.glb')) {
      return (
        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2L3 7v6l7 5 7-5V7l-7-5zM8.5 8.5L10 7l1.5 1.5L10 10 8.5 8.5z" />
          </svg>
        </div>
      );
    }

    switch (mediaType) {
      case 'image':
        return <ImageIcon className="h-4 w-4" />;
      case 'video':
        return <FileIcon className="h-4 w-4" />;
      default:
        return <FileIcon className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-2" data-selector-id={selectorId}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <style>{`
          [data-radix-popper-content-wrapper] {
            z-index: 100 !important;
          }
          [data-radix-dialog-overlay] {
            z-index: 99 !important;
          }
        `}</style>
        <DialogTrigger asChild>
          <div
            className="relative cursor-pointer"
            onMouseEnter={(e) => {
              const bgElement = e.currentTarget.querySelector('.hover-bg') as HTMLElement;
              if (bgElement) bgElement.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              const bgElement = e.currentTarget.querySelector('.hover-bg') as HTMLElement;
              if (bgElement) bgElement.style.opacity = '0';
            }}
          >
            <div className="hover-bg absolute -inset-1 bg-gradient-to-r from-primary/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur opacity-0 transition-opacity duration-300"></div>
            <div className="relative w-full py-1 px-3 bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-300/50 dark:border-slate-600/30 rounded-2xl hover:border-pink-500/50 transition-all duration-300">
              {selectedFiles.length > 0 ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">{selectedFiles.length} file(s) selected</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Click to change selection</p>
                    </div>
                  </div>
                  <div className="flex -space-x-2">
                    {selectedFiles.slice(0, 3).map((file, index) => (
                      <div key={file.id} className="w-8 h-8 rounded-lg overflow-hidden border-2 border-slate-600 bg-slate-700">
                        {(file.media_type === 'image' || 
                          (file.media_type === 'other' && (file.mime_type === 'model/gltf-binary' || file.original_filename?.endsWith('.glb')))) 
                          && file.thumbnail_url ? (
                          <AuthenticatedImage
                            src={file.media_type === 'other' ? `/api/v1/media/${file.id}/thumbnail` : file.thumbnail_url}
                            alt={file.title || file.original_filename}
                            className="w-full h-full object-cover"
                            clientId={currentClient?.id}
                          />
                        ) : (
                          <div className="w-full h-full bg-slate-600 flex items-center justify-center">
                            {file.mime_type === 'model/gltf-binary' || file.original_filename?.endsWith('.glb') ? (
                              <svg className="w-3 h-3 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2L3 7v6l7 5 7-5V7l-7-5zM8.5 8.5L10 7l1.5 1.5L10 10 8.5 8.5z"/>
                              </svg>
                            ) : (
                              <FileIcon className="w-3 h-3 text-slate-400" />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {selectedFiles.length > 3 && (
                      <div className="w-8 h-8 rounded-lg bg-slate-600 border-2 border-slate-600 flex items-center justify-center">
                        <span className="text-xs text-slate-300 font-medium">+{selectedFiles.length - 3}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className={`w-5 h-5 bg-gradient-to-br ${themeColors.gradient} rounded-lg flex items-center justify-center mx-auto mb-1`}>
                    <Upload className="w-3 h-3 text-white" />
                  </div>
                  <p className="font-semibold text-slate-800 dark:text-slate-200 text-sm mb-0">{placeholder}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">Browse your media library</p>
                </div>
              )}
            </div>
          </div>
        </DialogTrigger>

        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl border-slate-200/50 dark:border-slate-600/50 shadow-2xl z-[100]">
          <DialogHeader className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl blur-xl"></div>
            <div className="relative flex items-center space-x-4 p-6 bg-slate-100/30 dark:bg-slate-800/30 rounded-2xl border border-slate-200/30 dark:border-slate-600/30">
              <div className="w-12 h-12 bg-gradient-to-br from-primary via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
                <Folder className="w-6 h-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-black bg-gradient-to-r from-slate-800 via-indigo-600 to-purple-600 dark:from-white dark:via-indigo-200 dark:to-purple-200 bg-clip-text text-transparent">
                  Media Library
                </DialogTitle>
                <DialogDescription className="text-slate-600 dark:text-slate-300 text-lg">
                  Choose files from your collection
                  {totalFiles > 0 && (
                    <span className="ml-2 text-indigo-600 dark:text-indigo-300">
                      ({mediaFiles.length} of {totalFiles} files shown)
                    </span>
                  )}
                  {acceptedTypes.length < 4 && (
                    <span className="ml-2 text-indigo-600 dark:text-indigo-300">
                      - {acceptedTypes.join(', ')} files
                    </span>
                  )}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-6 flex-grow overflow-hidden flex flex-col">
            {/* Search Bar */}
            <motion.div
              className="relative flex items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex-1 relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-2xl blur opacity-0 focus-within:opacity-100 transition-opacity duration-300"></div>
                <div className="relative flex items-center bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-300/50 dark:border-slate-600/30 rounded-2xl px-4 py-3 focus-within:border-indigo-500/50 transition-all duration-300">
                  <Search className="h-5 w-5 text-slate-400 mr-3" />
                  <input
                    type="text"
                    placeholder="Search your media files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 bg-transparent outline-none placeholder:text-slate-500 dark:placeholder:text-slate-400 text-slate-800 dark:text-slate-200 text-base"
                  />
                </div>
              </div>
              
              {/* Upload button when files exist */}
              {allMediaFiles.length > 0 && (
                <Button
                  onClick={() => document.getElementById('file-upload-input-header')?.click()}
                  disabled={isLoading}
                  className="bg-gradient-to-r from-primary via-purple-600 to-pink-600 hover:from-primary/90 hover:via-purple-500 hover:to-pink-500 text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-pink-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex-shrink-0"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Upload className="h-5 w-5" />
                  )}
                </Button>
              )}
            </motion.div>

            {/* File Grid */}
            {isLoading ? (
              <motion.div
                className="flex flex-col items-center justify-center py-20 flex-grow"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative mb-6">
                  <div className="w-16 h-16 border-4 border-slate-700/30 border-t-indigo-500 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" style={{ animationDelay: '0.15s' }}></div>
                </div>
                <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">Loading your media</h3>
                <p className="text-slate-600 dark:text-slate-400">Organizing your creative assets...</p>
              </motion.div>
            ) : (
              <motion.div
                className="overflow-y-auto flex-grow pr-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                  // Load more when user scrolls to within 200px of bottom
                  if (scrollHeight - scrollTop - clientHeight < 200 && hasNextPage && !isLoadingMore) {
                    loadMoreFiles();
                  }
                }}
              >
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                  <AnimatePresence>
                    {mediaFiles.map((file, index) => {
                      const isSelected = internalSelection.some(f => f.id === file.id);
                      return (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className="group relative"
                        >

                          <div
                            className={`relative cursor-pointer bg-white/40 dark:bg-slate-800/40 backdrop-blur-xl rounded-2xl border transition-all duration-300 overflow-hidden ${isSelected
                              ? 'border-indigo-500/50 shadow-lg shadow-indigo-500/20'
                              : 'border-slate-300/30 dark:border-slate-600/30 hover:border-slate-400/50 dark:hover:border-slate-500/50'
                              }`}
                            onClick={() => handleFileToggle(file)}
                          >
                            {/* Selection Indicator */}
                            <div className="absolute top-3 right-3 z-10">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${isSelected
                                ? 'bg-indigo-500 border-indigo-500'
                                : 'bg-white/50 dark:bg-slate-700/50 border-slate-400 dark:border-slate-500 backdrop-blur-sm'
                                }`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </div>

                            {/* File Preview */}
                            <div className="aspect-square relative overflow-hidden">
                              {(file.media_type === 'image' ||
                                (file.media_type === 'other' && (file.mime_type === 'model/gltf-binary' || file.original_filename?.endsWith('.glb'))))
                                && file.thumbnail_url ? (
                                <AuthenticatedImage
                                  src={file.media_type === 'other' ? `/api/v1/media/${file.id}/thumbnail` : file.thumbnail_url}
                                  alt={file.title || file.original_filename}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  clientId={currentClient?.id}
                                />
                              ) : (
                                <div className="w-full h-full bg-slate-200/30 dark:bg-slate-700/30 flex flex-col items-center justify-center">
                                  {getFileIcon(file.media_type, file.mime_type, file.original_filename)}
                                  <span className="text-xs text-slate-600 dark:text-slate-400 mt-2 font-medium">
                                    {file.mime_type === 'model/gltf-binary' || file.original_filename?.endsWith('.glb') ? '3D MODEL' : file.media_type.toUpperCase()}
                                  </span>
                                </div>
                              )}

                              {/* Hover Overlay */}

                            </div>

                            {/* File Info */}
                            <div className="p-4">
                              <h4 className="font-semibold text-sm text-slate-800 dark:text-slate-200 truncate mb-1">
                                {file.title || file.original_filename}
                              </h4>
                              <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
                                <span>{formatFileSize(file.file_size)}</span>
                                {file.width && file.height && (
                                  <span>{file.width}×{file.height}</span>
                                )}
                              </div>
                              <div className="mt-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-200/50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300 border border-slate-300/50 dark:border-slate-600/50">
                                  {file.media_type}
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )
                    })}
                  </AnimatePresence>
                </div>

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">Loading more files...</span>
                  </div>
                )}

                {/* Load more button (fallback for users who prefer clicking) */}
                {hasNextPage && !isLoadingMore && mediaFiles.length > 0 && (
                  <div className="flex items-center justify-center py-8">
                    <Button
                      variant="outline"
                      onClick={loadMoreFiles}
                      className="flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      Load More Files
                    </Button>
                  </div>
                )}

                {/* End of results indicator */}
                {!hasNextPage && mediaFiles.length > 0 && (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-full">
                      All files loaded ({mediaFiles.length} total)
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Empty State */}
            {mediaFiles.length === 0 && !isLoading && (
              <motion.div
                className="text-center py-20 flex-grow flex flex-col items-center justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative mb-8">
                  <div className="w-20 h-20 bg-slate-700/30 rounded-full flex items-center justify-center mx-auto">
                    <FileIcon className="w-10 w-10 text-slate-500" />
                  </div>
                  <div className="absolute -inset-2 bg-gradient-to-r from-slate-500/10 via-indigo-500/10 to-purple-500/10 rounded-full blur-xl"></div>
                </div>
                <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-3">
                  {searchTerm ? 'No matching files' : 'No files found'}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-lg mb-8 max-w-md">
                  {searchTerm
                    ? 'Try adjusting your search terms or browse all files.'
                    : 'Upload some files to your media library to get started.'
                  }
                </p>
                <Button
                  onClick={() => document.getElementById('file-upload-input')?.click()}
                  disabled={isLoading}
                  className="bg-gradient-to-r from-primary via-purple-600 to-pink-600 hover:from-primary/90 hover:via-purple-500 hover:to-pink-500 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-pink-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                      Uploading...
                      <Sparkles className="ml-3 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      <Upload className="mr-3 h-5 w-5" />
                      Upload Files
                      <Sparkles className="ml-3 h-4 w-4" />
                    </>
                  )}
                </Button>
                <input
                  type="file"
                  id="file-upload-input"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    if (e.target.files && currentClient) {
                      setIsLoading(true);
                      try {
                        await mediaService.uploadFiles(Array.from(e.target.files), currentClient.id);
                        // Reset and refresh the media files list
                        setAllMediaFiles([]);
                        setCurrentPage(1);
                        setHasNextPage(true);
                        await fetchMediaFiles(1, true);
                        // Clear the input so the same file can be uploaded again if needed
                        e.target.value = '';
                      } catch (error) {
                        console.error('Error uploading files:', error);
                      } finally {
                        setIsLoading(false);
                      }
                    }
                  }}
                />
              </motion.div>
            )}
          </div>

          {/* Footer */}
          <motion.div
            className="flex justify-between items-center pt-6 border-t border-slate-200/30 dark:border-slate-600/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="text-slate-600 dark:text-slate-400">
              {internalSelection.length > 0 && (
                <span>{internalSelection.length} file{internalSelection.length !== 1 ? 's' : ''} selected</span>
              )}
            </div>
            <div className="flex space-x-3">
              <Button
                variant="ghost"
                onClick={() => setIsOpen(false)}
                className="text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 px-6 py-2 rounded-xl"
              >
                Cancel
              </Button>

              <Button
                onClick={handleConfirmSelection}
                className={`bg-gradient-to-r ${themeColors.buttonGradient} ${themeColors.buttonHover} text-white px-8 py-2 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg ${themeColors.shadow}`}
              >
                <Check className="mr-2 h-4 w-4" />
                Done {internalSelection.length > 0 && `(${internalSelection.length})`}
              </Button>
            </div>
          </motion.div>
          
          {/* Hidden input for header upload button */}
          <input
            type="file"
            id="file-upload-input-header"
            multiple
            className="hidden"
            onChange={async (e) => {
              if (e.target.files && currentClient) {
                setIsLoading(true);
                try {
                  await mediaService.uploadFiles(Array.from(e.target.files), currentClient.id);
                  // Reset and refresh the media files list
                  setAllMediaFiles([]);
                  setCurrentPage(1);
                  setHasNextPage(true);
                  await fetchMediaFiles(1, true);
                  // Clear the input so the same file can be uploaded again if needed
                  e.target.value = '';
                } catch (error) {
                  console.error('Error uploading files:', error);
                } finally {
                  setIsLoading(false);
                }
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
